import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getAllPosts } from "@/lib/blog";
import { PageHead } from "@/components/seo/PageHead";
import { generateBreadcrumbs } from "@/lib/seo";

export default function Blog() {
  const posts = getAllPosts();

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Cosmos Wave",
    "description": "Guides Amazon, stratégies Ads, SEO insights et actualités e-commerce. Expertise SPN et Creative Strategy.",
    "url": "https://www.cosmos-wave.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": "https://www.cosmos-wave.com"
    }
  };

  useEffect(() => {
    // Inject structured data for blog
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(blogSchema);
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Filter published posts in production
  const publishedPosts = posts.filter(post => {
    if (window.location.hostname === 'www.cosmos-wave.com') {
      return post.status === 'published';
    }
    return true; // Show all in development
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHead
        title="Blog Amazon — Cosmos Wave | Guides SEO, Ads & E-commerce Insights"
        description="Guides Amazon SEO, stratégies Ads, insights e-commerce et actualités marketplace. Expertise SPN pour optimiser vos performances."
        canonical="https://www.cosmos-wave.com/blog"
        pageType="website"
      />
      
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-primary/10" />
        <div className="container mx-auto px-6 text-center relative">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
            <span className="text-primary font-medium text-sm">Blog & Insights</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary via-primary/90 to-primary/70 bg-clip-text text-transparent">
              Blog Amazon
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
            Guides SEO Amazon, stratégies Ads, insights e-commerce et actualités marketplace. 
            Expertise <span className="text-primary font-medium">SPN</span> pour optimiser vos performances.
          </p>
          
          <div className="flex justify-center">
            <Badge variant="outline" className="text-muted-foreground">
              {publishedPosts.length} articles disponibles
            </Badge>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {publishedPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {publishedPosts.map((post) => (
                <Card key={post.slug} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardHeader className="p-0">
                    {post.cover && (
                      <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/5 rounded-t-lg flex items-center justify-center">
                        <img 
                          src={post.cover} 
                          alt={post.title}
                          className="w-full h-full object-cover rounded-t-lg"
                        />
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
                        <span>{new Date(post.datePublished).toLocaleDateString('fr-FR')}</span>
                        {post.status === 'draft' && (
                          <Badge variant="secondary" className="text-xs">
                            Brouillon
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-xl mb-3 group-hover:text-primary transition-colors">
                        {post.title}
                      </CardTitle>
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                        {post.description}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.slice(0, 3).map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-2 group-hover:gap-3 transition-all"
                    >
                      Lire l'article
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Articles en préparation</h3>
                <p className="text-muted-foreground">
                  Notre équipe d'experts prépare des contenus exclusifs sur l'écosystème Amazon. 
                  Restez connectés pour les prochaines publications !
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 via-primary/5 to-background relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative">
          <h2 className="text-3xl font-bold mb-4">
            Besoin d'une stratégie personnalisée ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Nos experts Amazon SPN vous accompagnent avec des solutions sur-mesure pour accelerer votre croissance.
          </p>
          <Link 
            to="/audit-gratuit"
            className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Obtenir un audit gratuit
          </Link>
        </div>
      </section>
    </div>
  );
}